import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactHero from "@/components/ContactHero";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "react-query";
import { getTeamMembers } from "@/api/teamService";
import { Card, CardContent } from "@/components/ui/card";
import { useCmsContent } from '@/hooks/useCmsContent';

export default function ContactPage() {
  const { toast } = useToast();
  const { getContent } = useCmsContent('contact');

  const contactDetails = [
    { label: "Telefoonnummer", value: getContent('contact_phone', '+31 6 27225807'), href: `tel:${getContent('contact_phone', '+31627225807').replace(/\s/g, '')}` },
    { label: "E-mailadres", value: getContent('contact_email', 'info@vermado.nl'), href: `mailto:${getContent('contact_email', 'info@vermado.nl')}` },
    { label: "Adresgegevens", value: getContent('contact_address', 'Netwerk 12 A\n1446WK Purmerend'), href: "https://maps.google.com/?q=Netwerk+12A,+1446+WK+Purmerend" },
    { label: "KVK-nummer", value: getContent('contact_kvk', '90898184'), href: null },
  ];

  const faqItems = [
    { question: getContent('faq_question_1', 'Wat zijn jullie openingstijden?'), answer: getContent('faq_answer_1', 'Wij zijn geopend van maandag t/m vrijdag van 08:00 tot 17:00 uur. In het weekend zijn wij gesloten.') },
    { question: getContent('faq_question_2', 'Kan ik zonder afspraak langskomen?'), answer: getContent('faq_answer_2', 'Wij werken uitsluitend op afspraak om u de beste service te kunnen garanderen. Maak eenvoudig een afspraak via ons contactformulier, telefoon of WhatsApp.') },
    { question: getContent('faq_question_3', 'Hoe kan ik het beste een afspraak maken?'), answer: getContent('faq_answer_3', 'U kunt een afspraak maken via ons contactformulier, telefonisch of via WhatsApp. Wij nemen dan zo spoedig mogelijk contact met u op.') },
    { question: getContent('faq_question_4', 'Wat zijn de betalingsmogelijkheden?'), answer: getContent('faq_answer_4', 'Wij accepteren PIN, contant en bankoverschrijving. Voor zakelijke klanten is betaling op factuur mogelijk.') },
  ];
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", licensePlate: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const { data: teamMembers = [], isLoading: isTeamLoading } = useQuery(
    'teamMembers',
    getTeamMembers,
    { staleTime: 300000, refetchOnWindowFocus: false }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast({ variant: "destructive", title: "Velden incompleet", description: "Vul minimaal uw naam en telefoonnummer in." });
      return;
    }
    setIsSubmitting(true);
    try {
      const supabaseUrl = 'https://lozejxkhsvwkksgzhrfd.supabase.co';
      const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvemVqeGtoc3Z3a2tzZ3pocmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0OTY4OTcsImV4cCI6MjA4MjA3Mjg5N30.GvcNibeMI-dF1dq4s4mq5Ap9Edspot9s5mdjUEWfZ0M';
      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || 'Server error');
      setIsSuccess(true);
    } catch (error) {
      toast({ variant: "destructive", title: "Er ging iets mis", description: error instanceof Error ? error.message : "Probeer het opnieuw." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <ContactHero />

      {/* Contact Details — domogo style: left info, right map */}
      <section className="bg-white py-20 md:py-28 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            {/* Left: tagline + details table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p
                className="text-black/40 text-xs tracking-[0.15em] uppercase mb-4"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Prestatie geeft{' '}
                <span className="relative inline-block">
                  <span className="absolute inset-0 bg-[#B1A06D]" style={{ transform: 'skewX(-4deg) rotate(-2deg)', borderRadius: '4px' }} />
                  <span className="relative z-10 text-black px-1.5 py-0.5 font-semibold text-[10px]">vrijheid</span>
                </span>
              </p>

              <h2
                className="text-black text-3xl md:text-4xl lg:text-[2.75rem] font-medium leading-[1.15] mb-12 tracking-[0.02em]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Onze contact gegevens
              </h2>

              <div className="space-y-0">
                {contactDetails.map((item, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-start py-5 border-t border-black/10 gap-1 sm:gap-0">
                    <span
                      className="text-black/40 text-sm sm:w-[180px] sm:flex-shrink-0"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {item.label}
                    </span>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.label === "Adresgegevens" ? "_blank" : "_self"}
                        rel="noopener noreferrer"
                        className="text-black text-sm font-medium whitespace-pre-line hover:text-[#B1A06D] transition-colors"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span
                        className="text-black text-sm font-medium whitespace-pre-line"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.value}
                      </span>
                    )}
                  </div>
                ))}
                <div className="border-t border-black/10" />
              </div>
            </motion.div>

            {/* Right: Google Maps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="rounded-2xl overflow-hidden aspect-[4/3] lg:aspect-square"
            >
              <iframe
                src="https://www.google.com/maps?q=Netwerk+12A,+1446+WK+Purmerend,+Netherlands&output=embed&z=13"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Vermado High-End Automotive"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form — domogo style */}
      <section className="bg-[#f2f2f0] py-20 md:py-28 lg:py-32">
        <div className="max-w-[700px] mx-auto px-8 md:px-12 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl p-8 md:p-10 lg:p-12"
            >
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <div className="w-14 h-14 bg-[#B1A06D] rounded-full flex items-center justify-center mb-4">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-black text-xl font-semibold mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Bericht verzonden!</h3>
                  <p className="text-black/50 text-sm mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    We nemen zo snel mogelijk contact met u op.
                  </p>
                  <button
                    onClick={() => { setIsSuccess(false); setFormData({ name: "", phone: "", email: "", licensePlate: "", message: "" }); }}
                    className="text-[#B1A06D] text-sm underline underline-offset-4 hover:text-[#B1A06D]/80 transition-colors"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Nieuw bericht
                  </button>
                </motion.div>
              ) : (
                <>
                  <h2
                    className="text-black text-2xl md:text-3xl font-medium mb-8 uppercase tracking-[0.04em]"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Stuur ons een bericht
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        name="name"
                        placeholder="Uw naam *"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="bg-[#f2f2f0] border-0 text-black placeholder:text-black/40 focus:ring-[#B1A06D] h-12 rounded-xl"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                        required
                      />
                      <Input
                        name="phone"
                        type="tel"
                        placeholder="Uw telefoonnummer *"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="bg-[#f2f2f0] border-0 text-black placeholder:text-black/40 focus:ring-[#B1A06D] h-12 rounded-xl"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        name="email"
                        type="email"
                        placeholder="Uw e-mailadres *"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="bg-[#f2f2f0] border-0 text-black placeholder:text-black/40 focus:ring-[#B1A06D] h-12 rounded-xl"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      />
                      <Input
                        name="licensePlate"
                        placeholder="Uw kenteken"
                        value={formData.licensePlate}
                        onChange={handleInputChange}
                        className="bg-[#f2f2f0] border-0 text-black placeholder:text-black/40 focus:ring-[#B1A06D] h-12 rounded-xl uppercase"
                        style={{ fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase' }}
                      />
                    </div>
                    <Textarea
                      name="message"
                      placeholder="Uw bericht"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="bg-[#f2f2f0] border-0 text-black placeholder:text-black/40 focus:ring-[#B1A06D] rounded-xl resize-none"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#B1A06D] hover:bg-[#B1A06D]/90 text-white text-xs tracking-[0.1em] uppercase px-8 py-4 rounded-full font-medium disabled:opacity-50 transition-colors"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {isSubmitting ? 'Bezig met verzenden...' : 'Versturen'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-white py-20 md:py-28 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 md:mb-16"
          >
            <h2
              className="text-black text-3xl md:text-4xl lg:text-[2.75rem] font-medium tracking-[0.08em] leading-[1.1] uppercase"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              ONS TEAM
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {isTeamLoading ? (
              [...Array(4)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#f2f2f0] rounded-2xl mb-4" />
                  <div className="h-4 bg-[#f2f2f0] rounded w-2/3 mx-auto mb-2" />
                  <div className="h-3 bg-[#f2f2f0] rounded w-1/2 mx-auto" />
                </div>
              ))
            ) : teamMembers.length > 0 ? (
              teamMembers.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group"
                >
                  <div className="w-full aspect-[3/4] rounded-2xl overflow-hidden mb-4 bg-[#f2f2f0]">
                    {member.image_url ? (
                      <div className="relative w-full h-full">
                        <img
                          src={member.image_url}
                          alt={member.name}
                          className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:opacity-0 group-hover:scale-105"
                        />
                        {member.hover_image_url && (
                          <img
                            src={member.hover_image_url}
                            alt={`${member.name}`}
                            className="absolute inset-0 w-full h-full object-cover opacity-0 scale-105 transition-all duration-500 group-hover:opacity-100 group-hover:scale-100"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-[#B1A06D] text-4xl font-bold">{member.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <h3
                    className="text-black text-sm font-semibold tracking-[0.08em] uppercase text-center"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {member.name}
                  </h3>
                  <p
                    className="text-black/40 text-xs tracking-[0.05em] text-center mt-1"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {member.position}
                  </p>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-black/40 text-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>Nog geen teamleden toegevoegd.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-20 md:py-28 lg:py-32 border-t border-black/5">
        <div className="max-w-[800px] mx-auto px-8 md:px-12 lg:px-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-black text-3xl md:text-4xl font-medium tracking-[0.08em] leading-[1.1] uppercase mb-12 md:mb-16"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            VEELGESTELDE VRAGEN
          </motion.h2>

          <div className="space-y-0">
            {faqItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="border-t border-black/10"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-6 text-left"
                >
                  <span
                    className="text-black text-sm md:text-base font-medium pr-8"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {item.question}
                  </span>
                  <span
                    className="text-black/40 text-xl flex-shrink-0 transition-transform duration-300"
                    style={{ transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)' }}
                  >
                    +
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: openFaq === i ? '200px' : '0px', opacity: openFaq === i ? 1 : 0 }}
                >
                  <p
                    className="text-black/50 text-sm leading-[1.75] pb-6"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {item.answer}
                  </p>
                </div>
              </motion.div>
            ))}
            <div className="border-t border-black/10" />
          </div>
        </div>
      </section>

      <ReviewsCarousel />

      <Footer />
    </div>
  );
}
